import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { api } from '../api/client';

export interface ChatState {
  conversation: any | null;
  loading: boolean;
  error: string | null;
}

const initialState: ChatState = {
  conversation: null,
  loading: false,
  error: null,
};

export const fetchConversation = createAsyncThunk(
  'chat/fetchConversation',
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.chat.getConversation();
      if (res.success) {
        return res.data;
      }
      return rejectWithValue(res.message || 'Failed to fetch conversation');
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to fetch conversation');
    }
  }
);

export const sendChatMessage = createAsyncThunk(
  'chat/sendChatMessage',
  async (
    { conversationId, text, imageUrl }: { conversationId: string; text: string; imageUrl?: string },
    { rejectWithValue }
  ) => {
    try {
      const res = await api.chat.sendMessage(conversationId, text, imageUrl || null);
      if (res.success) {
        return res.data;
      }
      return rejectWithValue(res.message || 'Failed to send message');
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to send message');
    }
  }
);

export const markAsRead = createAsyncThunk(
  'chat/markAsRead',
  async (conversationId: string, { rejectWithValue }) => {
    try {
      const res = await api.chat.markAsRead(conversationId);
      if (res.success) {
        return conversationId;
      }
      return rejectWithValue(res.message || 'Failed to mark chat as read');
    } catch (err: any) {
      return rejectWithValue(err.message || 'Failed to mark chat as read');
    }
  }
);

const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // fetchConversation
      .addCase(fetchConversation.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchConversation.fulfilled, (state, action) => {
        state.loading = false;
        state.conversation = action.payload;
      })
      .addCase(fetchConversation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // sendChatMessage
      .addCase(sendChatMessage.fulfilled, (state, action) => {
        if (state.conversation && action.payload) {
          state.conversation.messages = [...(state.conversation.messages || []), action.payload];
        }
      })
      // markAsRead
      .addCase(markAsRead.fulfilled, (state) => {
        if (state.conversation) {
          state.conversation.unreadCount = 0;
        }
      });
  },
});

export default chatSlice.reducer;
